import { useState, useEffect } from 'react';
import { subscriptionPlans } from '@/lib/mock-data';
import type { SubscriptionTier } from '@/lib/types';
import { TestimonialCarousel } from '@/components/paywall/testimonial-carousel';
import { PlanToggle } from '@/components/paywall/plan-toggle';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { X, Loader2 } from 'lucide-react';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  onSubscribe: () => void;
}

export function PaywallModal({ open, onClose, onSubscribe }: PaywallModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier>('pro');
  const [isLoading, setIsLoading] = useState(false);

  const plan = subscriptionPlans.find((p) => p.id === selectedPlan);

  useEffect(() => {
    if (open) {
      logAnalyticsEvent('paywall_view');
    }
  }, [open]);

  const handleSubscribe = async () => {
    setIsLoading(true);
    logAnalyticsEvent('subscribe_click', { plan: selectedPlan });
    await new Promise(resolve => setTimeout(resolve, 500));

    if (plan?.polarProductId) {
      window.location.href = `/api/checkout?products=${plan.polarProductId}`;
      return;
    }

    console.error('Polar Product ID missing for plan:', selectedPlan);
    alert('Configuration Error: Product ID missing. Please contact support.');
    setIsLoading(false);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl p-0" hideClose>
        <div className="h-full overflow-y-auto">
          <div className="max-w-md mx-auto px-6 py-8 space-y-6">
            {/* Close Button */}
            <div className="flex justify-start">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Title */}
            <SheetHeader>
              <div className="text-center space-y-2">
                <SheetTitle className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-archivo-black)' }}>
                  Deepcheck Pro
                </SheetTitle>
                <p className="text-xl text-gray-600">Scan Online Presences</p>
              </div>
            </SheetHeader>

            {/* Testimonials */}
            <TestimonialCarousel />

            {/* Cancel Anytime */}
            <p className="text-center text-gray-600 text-base">Cancel anytime</p>

            {/* Plan Toggle */}
            <PlanToggle selected={selectedPlan} onSelect={setSelectedPlan} />

            {/* Pricing Display */}
            {plan && (
              <div className="flex justify-between items-start px-2">
                <div className="text-left space-y-1">
                  <div className="text-2xl font-bold text-gray-900">
                    {plan.id === 'pro' ? 'Pro' : 'Max'}
                  </div>
                  <div className="text-gray-600 text-sm">
                    {plan.periodLabel === 'per Month' ? 'Only' : 'just'} ₺
                    {plan.price.toFixed(2).replace('.', ',')} {plan.periodLabel}
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <div className="text-2xl font-bold text-gray-900">
                    ₺{plan.dailyRate.toFixed(2).replace('.', ',')}
                  </div>
                  <div className="text-gray-500 text-sm">{plan.dailyRateLabel}</div>
                </div>
              </div>
            )}

            {/* Subscribe Button */}
            <Button
              onClick={handleSubscribe}
              disabled={isLoading}
              className="w-full h-14 bg-black hover:bg-gray-800 text-white font-semibold text-lg disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                'Search Now'
              )}
            </Button>

          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
